import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getReservationByUuid, reservationQrUrl } from '../services/api';
import { formatDate, formatPrice, reservationStatuses } from '../utils/format';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  CheckCircle2, Download, Share2, Calendar, Clock,
  Armchair, Mail, Loader2, ArrowLeft, RefreshCw, Timer
} from 'lucide-react';

const POLL_INTERVAL_MS = 4000;
const POLL_MAX_ATTEMPTS = 30; // ~2 min

const ReservationSuccess = () => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const load = useCallback(async () => {
    try {
      const res = await getReservationByUuid(uuid);
      setData(res);
      setError(null);
    } catch (err) {
      setError(err.message || 'No se pudo cargar la reserva');
    } finally {
      setLoading(false);
    }
  }, [uuid]);

  useEffect(() => { load(); }, [load]);

  // Poll while pending (waiting for webhook to confirm)
  useEffect(() => {
    if (!data?.reservation) return;
    if (data.reservation.status === 'confirmed') return;
    if (attempts >= POLL_MAX_ATTEMPTS) return;
    const t = setTimeout(() => {
      setAttempts(a => a + 1);
      load();
    }, POLL_INTERVAL_MS);
    return () => clearTimeout(t);
  }, [data, attempts, load]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-primary animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">{error || 'Reserva no encontrada'}</p>
          <Button onClick={() => navigate('/')} variant="outline">Volver al inicio</Button>
        </div>
      </div>
    );
  }

  const { reservation, event } = data;
  const statusConf = reservationStatuses[reservation.status] || reservationStatuses.pending;
  const isConfirmed = reservation.status === 'confirmed';
  const isRejected = reservation.status === 'rejected' || reservation.status === 'expired' || reservation.status === 'cancelled';
  const qrUrl = reservationQrUrl(reservation.uuid);

  const handleDownload = async () => {
    try {
      const resp = await fetch(qrUrl);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `reserva-${reservation.uuid}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
    }
  };

  const handleShareWhatsApp = () => {
    const text = `Mi reserva para ${event.name} ${formatDate(event.date)} · ${reservation.chairIds?.length || 0} silla(s)\nID: ${reservation.uuid}\nQR: ${window.location.href}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const handleNativeShare = async () => {
    if (!navigator.share) return handleShareWhatsApp();
    try {
      await navigator.share({
        title: `Reserva ${event.name}`,
        text: `Mi entrada para ${event.name}`,
        url: window.location.href,
      });
    } catch { /* ignore */ }
  };

  return (
    <div data-testid="reservation-success-page" className="min-h-screen bg-[#050505] px-4 sm:px-6 py-6 sm:py-10">
      <div className="max-w-md mx-auto space-y-5">
        <button onClick={() => navigate('/')} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-3.5 h-3.5" /> Volver al inicio
        </button>

        {/* Status Card */}
        <div className={`rounded-xl border p-5 sm:p-6 text-center
          ${isConfirmed
            ? 'bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border-emerald-500/30'
            : isRejected
              ? 'bg-red-500/5 border-red-500/30'
              : 'bg-amber-500/5 border-amber-500/30'}`}>
          {isConfirmed ? (
            <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-400 mb-2" />
          ) : isRejected ? (
            <Timer className="w-12 h-12 mx-auto text-red-400 mb-2" />
          ) : (
            <Loader2 className="w-12 h-12 mx-auto text-amber-400 mb-2 animate-spin" />
          )}
          <h1 className="text-xl sm:text-2xl font-serif font-semibold text-foreground">
            {isConfirmed ? '¡Pago confirmado!' : isRejected ? 'Reserva cancelada' : 'Procesando pago...'}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {isConfirmed
              ? 'Tu reserva está lista. Guarda tu QR.'
              : isRejected
                ? 'Esta reserva no pudo completarse.'
                : 'Estamos esperando confirmación del banco. Esta página se actualizará automáticamente.'}
          </p>
          <Badge className={`mt-3 ${statusConf.color}`}>{statusConf.label}</Badge>
        </div>

        {/* QR Card — only when confirmed */}
        {isConfirmed && (
          <div className="bg-secondary border border-border rounded-xl p-5 sm:p-6 space-y-4">
            <div className="bg-white rounded-lg p-4 flex items-center justify-center">
              <img
                data-testid="reservation-qr"
                src={qrUrl}
                alt="QR de tu reserva"
                className="w-full max-w-[260px] aspect-square object-contain"
              />
            </div>
            <p className="text-[11px] text-muted-foreground text-center break-all">ID: {reservation.uuid}</p>

            <div className="grid grid-cols-2 gap-2">
              <Button
                data-testid="download-qr-btn"
                onClick={handleDownload}
                className="w-full bg-primary hover:bg-accent"
              >
                <Download className="w-4 h-4 mr-2" />Descargar
              </Button>
              <Button
                data-testid="share-qr-btn"
                onClick={handleNativeShare}
                variant="outline"
                className="w-full border-border text-foreground"
              >
                <Share2 className="w-4 h-4 mr-2" />Compartir
              </Button>
            </div>
            <button
              data-testid="share-whatsapp-btn"
              onClick={handleShareWhatsApp}
              className="w-full inline-flex items-center justify-center gap-2 text-xs text-emerald-400 hover:text-emerald-300"
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 0 1 8.413 3.488 11.824 11.824 0 0 1 3.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z"/></svg>
              Enviar a WhatsApp
            </button>
          </div>
        )}

        {/* Pending hint */}
        {!isConfirmed && !isRejected && (
          <button
            onClick={() => { setAttempts(0); load(); }}
            className="w-full inline-flex items-center justify-center gap-2 text-xs text-muted-foreground hover:text-foreground py-2"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Actualizar manualmente
          </button>
        )}

        {/* Reservation Details */}
        <div className="bg-secondary border border-border rounded-xl p-4 sm:p-5 space-y-3">
          <h2 className="text-sm font-medium text-foreground">Detalles de tu reserva</h2>
          <div className="flex items-center gap-3">
            {event.image && <img src={event.image} alt={event.name} className="w-14 h-14 rounded object-cover shrink-0" />}
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground truncate">{event.name}</p>
              <p className="text-xs text-muted-foreground flex items-center gap-1.5"><Calendar className="w-3 h-3" />{formatDate(event.date)}</p>
              <p className="text-xs text-muted-foreground flex items-center gap-1.5"><Clock className="w-3 h-3" />{event.time} hrs</p>
            </div>
          </div>

          <div className="border-t border-border pt-3 space-y-1.5 text-sm">
            <div className="flex justify-between text-muted-foreground">
              <span className="flex items-center gap-1.5"><Armchair className="w-3.5 h-3.5" />Sillas</span>
              <span className="text-foreground">{reservation.chairIds?.length || 0}</span>
            </div>
            <div className="flex justify-between font-medium pt-1 border-t border-border">
              <span className="text-foreground">Total pagado</span>
              <span className="text-primary">{formatPrice(reservation.totalPrice, event.currency)}</span>
            </div>
          </div>

          <div className="border-t border-border pt-3 text-xs text-muted-foreground space-y-1">
            <p className="flex items-center gap-1.5 truncate"><Mail className="w-3 h-3" />{reservation.email}</p>
            <p className="text-muted-foreground/70">También enviamos el QR a tu correo.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReservationSuccess;
